import { UUID } from "@bitory-gmbh/nidavellir";
import { documentDbPut } from "@/shared/lib/storage/indexdb/document-db";
import { GSMDocumentKind } from "@/services/backend/documents/types/kind";
import t from "@/lang/lang";
import {
  Query,
  useDocumentDBGetAll,
} from "@/shared/lib/storage/indexdb/document-db-hooks";
import {
  useHtzStructuralAssessmentCatalogShowActiveCatalogQuery,
  useHtzStructuralAssessmentCatalogShowQuery,
  useLazyHtzStructuralAssessmentCatalogShowActiveCatalogQuery,
  useLazyHtzStructuralAssessmentCatalogShowQuery,
} from "@/services/backend/htz/inspection/api/structural-assessment-catalog-api";
import { StructuralAssessmentCatalog } from "@/services/backend/htz/inspection/types/structural-assessment-catalog";
import { useStaleContext } from "./stale-context";
import { OfflineState, useOfflineContext } from "./offline-context";

export function useStructuralAssessmentCatalogOffline(
  workOrderId: UUID,
  companyId: string,
) {
  const { setSynced } = useOfflineContext();
  const { addStale } = useStaleContext();
  const [loadActive] =
    useLazyHtzStructuralAssessmentCatalogShowActiveCatalogQuery();
  const [loadCatalog] = useLazyHtzStructuralAssessmentCatalogShowQuery();

  const prepMsg = t("Speichere Konstruktionsbewertungskatalog ...");
  const doneMsg = t("Konstruktionsbewertungskatalog");

  const prepare = async () => {
    const activeResult = await loadActive({ companyId });
    if (activeResult.error) throw activeResult.error;
    if (!activeResult.data?.catalogId)
      throw new Error("No active catalog found");

    const catalogResult = await loadCatalog({
      id: activeResult.data.catalogId,
    });
    if (catalogResult.error) throw catalogResult.error;

    addStale("structuralAssessmentCatalog");
    setSynced((prev) => [...prev, prepMsg]);
    await documentDbPut(
      GSMDocumentKind.WorkOrder,
      workOrderId,
      "structuralAssessmentCatalog",
      catalogResult.data!,
    );
    setSynced((prev) => prev.map((p) => (p === prepMsg ? doneMsg : p)));
  };

  return { prepare };
}

export function useGetStructuralAssessmentCatalog(
  workOrderId: string,
  companyId: string,
): Query<StructuralAssessmentCatalog> {
  const { offlineState } = useOfflineContext();

  const {
    data: activeSACatalog,
    isLoading: activeSACatalogIsLoading,
    error: activeSACatalogError,
  } = useHtzStructuralAssessmentCatalogShowActiveCatalogQuery(
    { companyId },
    { skip: !companyId },
  );

  const {
    data: structuralAssessmentCatalog,
    isLoading: structuralAssessmentCatalogIsLoading,
    error: structuralAssessmentCatalogError,
  } = useHtzStructuralAssessmentCatalogShowQuery(
    {
      id: activeSACatalog?.catalogId ?? "",
    },
    {
      skip:
        activeSACatalog === undefined || activeSACatalog?.catalogId === null,
    },
  );

  const {
    data: indexedDBData,
    isLoading: indexedDBIsLoading,
    error: indexedDBError,
  } = useDocumentDBGetAll<StructuralAssessmentCatalog>(
    GSMDocumentKind.WorkOrder,
    workOrderId,
    "structuralAssessmentCatalog",
  );

  switch (offlineState) {
    case OfflineState.ONLINE:
      return {
        data: structuralAssessmentCatalog,
        isLoading:
          activeSACatalogIsLoading || structuralAssessmentCatalogIsLoading,
        error: activeSACatalogError || structuralAssessmentCatalogError,
      };
    case OfflineState.OFFLINE:
      return {
        data: indexedDBData ? indexedDBData[0] : undefined,
        isLoading: indexedDBIsLoading,
        error: indexedDBError,
      };
    default:
      return {
        data: undefined,
        isLoading: false,
        error: { status: 500 },
      };
  }
}
